import React, { useState, useEffect } from 'react';

// US States list
const US_STATES = [
  '', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Countries list (common ones)
const COUNTRIES = [
  '', 'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 
  'Netherlands', 'Belgium', 'Spain', 'Italy', 'Australia', 'New Zealand',
  'Japan', 'South Korea', 'Brazil', 'Mexico', 'Argentina', 'Sweden',
  'Norway', 'Denmark', 'Finland', 'Switzerland', 'Austria', 'Portugal',
  'Ireland', 'Poland', 'Czech Republic', 'Greece', 'Turkey', 'India',
  'China', 'Singapore', 'Thailand', 'South Africa', 'Other'
];

const EditProfileModal = ({ user, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    city: '',
    state: '',
    country: '',
    user_type: 'regular', // regular, dj, producer, streamer, or combinations
    open_for_bookings: false,
    open_for_collabs: false,
    accepting_demos: false,
    demo_submission_email: ''
  });
  const [userTypes, setUserTypes] = useState({
    dj: false,
    producer: false,
    streamer: false,
    mod: false,
    label: false,
    ar: false,
    venue_owner: false,
    promoter: false
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [eventEmailAlerts, setEventEmailAlerts] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [charCounts, setCharCounts] = useState({
    name: 0,
    bio: 0,
    city: 0,
    state: 0,
    country: 0
  });

  // Character limits
  const limits = {
    name: 15,
    bio: 500,
    city: 50,
    state: 50,
    country: 50
  };

  useEffect(() => {
    if (user && isOpen) {
      // Parse location if it exists (format: "city, state country")
      let city = '', state = '', country = '';
      if (user.location) {
        // Split by comma first to get city
        const parts = user.location.split(',').map(s => s.trim());
        if (parts.length >= 1) {
          city = parts[0] || '';
        }
        if (parts.length >= 2) {
          // Everything after the comma is "state country"
          const stateCountry = parts[1].trim();
          
          // Try to match against known countries
          const matchedCountry = COUNTRIES.find(c => 
            c && stateCountry.toLowerCase().includes(c.toLowerCase())
          );
          
          if (matchedCountry) {
            country = matchedCountry;
            // State is what's left after removing the country
            state = stateCountry.replace(new RegExp(matchedCountry, 'i'), '').trim();
          } else {
            // If no country match, assume last word is country
            const words = stateCountry.split(' ');
            if (words.length > 1) {
              country = words[words.length - 1];
              state = words.slice(0, -1).join(' ');
            } else {
              state = stateCountry;
            }
          }
        }
      }
      
      // Parse user_type into checkboxes
      const currentUserType = user.user_type || 'regular';
      const types = {
        dj: currentUserType.includes('dj'),
        producer: currentUserType.includes('producer'),
        streamer: currentUserType.includes('streamer'),
        mod: currentUserType.includes('mod'),
        label: currentUserType.includes('label'),
        ar: currentUserType.includes('ar'),
        venue_owner: currentUserType.includes('venue_owner'),
        promoter: currentUserType.includes('promoter')
      };
      setUserTypes(types);
      
      setFormData({
        name: user.name || '',
        bio: user.bio || '',
        city: city,
        state: state,
        country: country,
        user_type: currentUserType,
        open_for_bookings: user.open_for_bookings || false,
        open_for_collabs: user.open_for_collabs || false,
        accepting_demos: user.accepting_demos || false,
        demo_submission_email: user.demo_submission_email || ''
      });
      setCharCounts({
        name: (user.name || '').length,
        bio: (user.bio || '').length,
        city: city.length,
        state: state.length,
        country: country.length
      });
      
      // Set avatar preview - check if it's a full URL or relative path
      const API = process.env.REACT_APP_BACKEND_URL || '';
      if (user.profile_picture) {
        if (user.profile_picture.startsWith('http')) {
          setAvatarPreview(user.profile_picture);
        } else {
          // Convert /uploads to /api/uploads for correct backend path
          const avatarPath = user.profile_picture.replace('/uploads/', '/api/uploads/');
          setAvatarPreview(`${API}${avatarPath}`);
        }
      } else {
        setAvatarPreview(null);
      }
      
      setAvatarFile(null);
      setError(null);
      setSuccess(null);

      // Load email notification prefs
      (async () => {
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const token = localStorage.getItem('beatseek_token');
          const r = await fetch(`${API}/api/users/me/email-prefs`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (r.ok) {
            const d = await r.json();
            setEventEmailAlerts(!d.event_email_opt_out);
            setWeeklyDigest(!d.digest_opt_out);
          }
        } catch (e) { /* keep default ON */ }
      })();
    }
  }, [user, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const maxLength = limits[name];
    
    // Special handling for country change - just update the country, preserve state
    if (name === 'country') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value
      }));
      setCharCounts(prev => ({ 
        ...prev, 
        [name]: value.length
      }));
      return;
    }
    
    // Enforce character limit for text inputs
    if (value.length <= maxLength) {
      setFormData(prev => ({ ...prev, [name]: value }));
      setCharCounts(prev => ({ ...prev, [name]: value.length }));
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Only PNG and JPG images are allowed.');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }

    setAvatarFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUserTypeChange = (type) => {
    setUserTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token');

      // Save email notification prefs first, independently — so the toggle sticks
      // even if the profile update below has an issue.
      try {
        await fetch(`${API}/api/users/me/email-prefs`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ event_email_opt_out: !eventEmailAlerts, digest_opt_out: !weeklyDigest })
        });
      } catch (e) { /* non-fatal */ }

      // Build user_type string from checkboxes
      const selectedTypes = Object.keys(userTypes).filter(type => userTypes[type]);
      const userTypeValue = selectedTypes.length > 0 ? selectedTypes.join(',') : 'regular';

      // Upload avatar first if there's a new one
      let avatarUrl = user.profile_picture;
      if (avatarFile) {
        const formDataAvatar = new FormData();
        formDataAvatar.append('file', avatarFile);

        const avatarResponse = await fetch(`${API}/api/users/upload-avatar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataAvatar
        });

        if (!avatarResponse.ok) {
          const errorData = await avatarResponse.json();
          throw new Error(errorData.detail || 'Avatar upload failed');
        }

        const avatarData = await avatarResponse.json();
        avatarUrl = avatarData.avatar_url;
      }

      // Update user profile
      // Format location properly: "city, state country" with proper spacing
      let locationStr = '';
      if (formData.city) {
        locationStr = formData.city;
        if (formData.state || formData.country) {
          locationStr += ', ';
          if (formData.state) {
            locationStr += formData.state + ' ';
          }
          if (formData.country) {
            locationStr += formData.country;
          }
        }
      } else if (formData.state || formData.country) {
        if (formData.state) {
          locationStr = formData.state;
          if (formData.country) {
            locationStr += ' ' + formData.country;
          }
        } else {
          locationStr = formData.country;
        }
      }
      
      const updateData = {
        name: formData.name,
        bio: formData.bio,
        location: locationStr.trim(),
        profile_picture: avatarUrl,
        user_type: userTypeValue,
        open_for_bookings: formData.open_for_bookings,
        open_for_collabs: formData.open_for_collabs,
        accepting_demos: formData.accepting_demos,
        demo_submission_email: formData.demo_submission_email || null
      };

      const response = await fetch(`${API}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Profile update failed');
      }

      const updatedUser = await response.json();

      setSuccess('Profile updated successfully!');
      
      // Call parent update handler
      if (onUpdate) {
        onUpdate(updatedUser);
      }

      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl border border-cyan-500/30 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-cyan-600 to-blue-600 p-4 rounded-t-xl flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            ✏️ Edit Profile
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-red-300 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Avatar Upload */}
          <div className="space-y-3">
            <label className="block text-cyan-300 font-semibold text-sm">
              Profile Picture
            </label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center text-3xl font-bold text-white overflow-hidden border-2 border-cyan-500/50">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  user?.name?.charAt(0).toUpperCase() || '?'
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer inline-block bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 px-2 py-1 text-xs rounded-lg border border-cyan-500/30 transition-colors">
                  <span>Choose Image</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
                <p className="text-gray-400 text-xs mt-2">PNG or JPG, max 5MB</p>
              </div>
            </div>
          </div>

          {/* Username */}
          <div className="space-y-2">
            <label className="block text-cyan-300 font-semibold text-sm">
              Username
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
              placeholder="Your username"
              required
              minLength={3}
            />
            <div className="text-right text-xs text-gray-400">
              {charCounts.name}/{limits.name} characters
            </div>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <label className="block text-cyan-300 font-semibold text-sm">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              rows={4}
              className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors resize-none"
              placeholder="Tell us about yourself..."
            />
            <div className="text-right text-xs text-gray-400">
              {charCounts.bio}/{limits.bio} characters
            </div>
          </div>

          {/* Location - City, State, Country */}
          <div className="space-y-2">
            <label className="block text-cyan-300 font-semibold text-sm">
              Location
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="City"
                />
                <div className="text-right text-xs text-gray-400 mt-1">
                  {charCounts.city}/{limits.city}
                </div>
              </div>
              <div>
                {formData.country === 'United States' ? (
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  >
                    <option value="">Select State</option>
                    {US_STATES.filter(s => s).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                      placeholder="State/Province"
                    />
                    <div className="text-right text-xs text-gray-400 mt-1">
                      {charCounts.state}/{limits.state}
                    </div>
                  </>
                )}
              </div>
              <div>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                >
                  <option value="">Select Country</option>
                  {COUNTRIES.filter(c => c).map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* User Type Selection */}
          <div className="space-y-3">
            <label className="block text-cyan-300 font-semibold text-sm">
              I am a... (select all that apply)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 bg-slate-800/30 border border-cyan-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-cyan-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.dj}
                  onChange={() => handleUserTypeChange('dj')}
                  className="w-4 h-4 text-cyan-600 bg-slate-700 border-cyan-500/30 rounded focus:ring-cyan-500"
                />
                <span className="text-white font-medium">🎧 DJ</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-cyan-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-cyan-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.producer}
                  onChange={() => handleUserTypeChange('producer')}
                  className="w-4 h-4 text-cyan-600 bg-slate-700 border-cyan-500/30 rounded focus:ring-cyan-500"
                />
                <span className="text-white font-medium">🎹 Producer</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-cyan-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-cyan-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.streamer}
                  onChange={() => handleUserTypeChange('streamer')}
                  className="w-4 h-4 text-cyan-600 bg-slate-700 border-cyan-500/30 rounded focus:ring-cyan-500"
                />
                <span className="text-white font-medium">📡 Streamer</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-red-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-red-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.mod}
                  onChange={() => handleUserTypeChange('mod')}
                  className="w-4 h-4 text-red-600 bg-slate-700 border-red-500/30 rounded focus:ring-red-500"
                />
                <span className="text-white font-medium">🛡️ Mod</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-amber-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-amber-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.label}
                  onChange={() => handleUserTypeChange('label')}
                  className="w-4 h-4 text-amber-600 bg-slate-700 border-amber-500/30 rounded focus:ring-amber-500"
                />
                <span className="text-white font-medium">💿 Record Label</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-purple-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-purple-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.ar}
                  onChange={() => handleUserTypeChange('ar')}
                  className="w-4 h-4 text-purple-600 bg-slate-700 border-purple-500/30 rounded focus:ring-purple-500"
                />
                <span className="text-white font-medium">🎤 A&R</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-emerald-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-emerald-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.venue_owner}
                  onChange={() => handleUserTypeChange('venue_owner')}
                  className="w-4 h-4 text-emerald-600 bg-slate-700 border-emerald-500/30 rounded focus:ring-emerald-500"
                />
                <span className="text-white font-medium">🏛️ Venue Owner</span>
              </label>
              <label className="flex items-center gap-2 bg-slate-800/30 border border-pink-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-pink-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={userTypes.promoter}
                  onChange={() => handleUserTypeChange('promoter')}
                  className="w-4 h-4 text-pink-600 bg-slate-700 border-pink-500/30 rounded focus:ring-pink-500"
                />
                <span className="text-white font-medium">📣 Promoter</span>
              </label>
            </div>
            {user?.role === 'admin' && (
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg px-3 py-2 text-sm">
                <span className="text-yellow-300 font-bold">👑 Admin Role:</span>
                <span className="text-yellow-200 ml-2">You have administrator privileges</span>
              </div>
            )}
          </div>

          {/* Availability Settings */}
          <div className="space-y-3">
            <label className="block text-cyan-300 font-semibold text-sm">
              Availability Settings
            </label>
            <p className="text-xs text-gray-400 -mt-1">
              Enable these to show badges on your profile and let others contact you
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 bg-slate-800/30 border border-green-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-green-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={formData.open_for_bookings}
                  onChange={(e) => setFormData({ ...formData, open_for_bookings: e.target.checked })}
                  className="w-5 h-5 text-green-600 bg-slate-700 border-green-500/30 rounded focus:ring-green-500"
                />
                <div>
                  <span className="text-white font-medium block">📅 Open for Bookings</span>
                  <span className="text-xs text-gray-400">Available for gigs & events</span>
                </div>
              </label>
              <label className="flex items-center gap-3 bg-slate-800/30 border border-purple-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-purple-500/40 transition-all">
                <input
                  type="checkbox"
                  checked={formData.open_for_collabs}
                  onChange={(e) => setFormData({ ...formData, open_for_collabs: e.target.checked })}
                  className="w-5 h-5 text-purple-600 bg-slate-700 border-purple-500/30 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="text-white font-medium block">🤝 Open for Collabs</span>
                  <span className="text-xs text-gray-400">Looking for collaborations</span>
                </div>
              </label>
            </div>
          </div>

          {/* Email Notifications */}
          <div className="space-y-3">
            <label className="block text-cyan-300 font-semibold text-sm">
              Email Notifications
            </label>
            <p className="text-xs text-gray-400 -mt-1">
              Choose which BEATSEEK emails you'd like to receive
            </p>
            <label
              data-testid="event-email-alerts-toggle"
              className="flex items-center gap-3 bg-slate-800/30 border border-cyan-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-cyan-500/40 transition-all"
            >
              <input
                type="checkbox"
                data-testid="event-email-alerts-checkbox"
                checked={eventEmailAlerts}
                onChange={(e) => setEventEmailAlerts(e.target.checked)}
                className="w-5 h-5 text-cyan-600 bg-slate-700 border-cyan-500/30 rounded focus:ring-cyan-500"
              />
              <div>
                <span className="text-white font-medium block">🎛️ Event email alerts</span>
                <span className="text-xs text-gray-400">Get an email when someone you follow or a friend posts an event, or when a DJ claims a slot on your event</span>
              </div>
            </label>
            <label
              data-testid="weekly-digest-toggle"
              className="mt-2 flex items-center gap-3 bg-slate-800/30 border border-cyan-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800/50 hover:border-cyan-500/40 transition-all"
            >
              <input
                type="checkbox"
                data-testid="weekly-digest-checkbox"
                className="w-5 h-5 accent-cyan-500"
                checked={weeklyDigest}
                onChange={(e) => setWeeklyDigest(e.target.checked)}
              />
              <div>
                <span className="text-white font-medium block">🎯 Weekly Track ID digest</span>
                <span className="text-xs text-gray-400">Every Sunday: the tracks you ID'd this week with BPM and key, ready for your next set</span>
              </div>
            </label>
          </div>

          {/* Accepting Demos Section - Only for Labels/A&R */}
          {(userTypes.label || userTypes.ar || user?.user_type === 'label' || user?.user_type === 'ar' || user?.user_type?.includes('label') || user?.user_type?.includes('ar')) && (
            <div className="space-y-3">
              <label className="block text-amber-400 font-semibold text-sm">
                💿 Demo Submissions (Label/A&R)
              </label>
              <p className="text-xs text-gray-400 -mt-1">
                Enable this to be featured in the &quot;Accepting Demos&quot; section and receive demo submissions
              </p>
              <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/30 rounded-lg p-4 space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.accepting_demos}
                    onChange={(e) => setFormData({ ...formData, accepting_demos: e.target.checked })}
                    className="w-5 h-5 text-amber-500 bg-slate-700 border-amber-500/30 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-white font-medium block">📬 Accepting Demos</span>
                    <span className="text-xs text-gray-400">Show in &quot;Accepting Demos&quot; featured section</span>
                  </div>
                </label>
                
                {formData.accepting_demos && (
                  <div className="space-y-2 animate-fadeIn">
                    <label className="block text-amber-300 text-xs font-medium">
                      Demo Submission Email
                    </label>
                    <input
                      type="email"
                      value={formData.demo_submission_email}
                      onChange={(e) => setFormData({ ...formData, demo_submission_email: e.target.value })}
                      placeholder="demos@yourlabel.com"
                      className="w-full bg-slate-800/50 border border-amber-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                    <p className="text-xs text-gray-500">
                      This email will be visible to producers looking to submit demos
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600/20 hover:bg-gray-600/30 text-gray-300 px-6 py-3 rounded-lg border border-gray-500/30 transition-colors font-medium"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-6 py-3 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
